import React, { FormEvent, useState } from "react";
import axios from "axios";
import { useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { ISenderDetail, VerificationPossibleEmails } from "shared";

import { Button } from "../../common/Atoms/Button";
import { Card } from "../../common/Atoms/Card";
import { CardContent } from "../../common/Atoms/CardContent";
import { FormLabel } from "../../common/Atoms/FormLabel";
import { Heading } from "../../common/Atoms/Typography/Heading";
import { Notification } from "../../common/Atoms/Notification";
import { Paragraph } from "../../common/Atoms/Typography/Paragraph";
import { PORTAL_AUTH_CODE_STORAGE_KEY } from "../constants";
import { env } from "../../common/lib/env";
import { classNames } from "../../common/lib/classNames";

export type PortalVerifyResource = `invoice` | `contact` | `quote`;

export type GeneratedRedactedEmailHint = Omit<VerificationPossibleEmails, `original`>;

export interface VerifyProps {
  resourceType: PortalVerifyResource;
  senderDetail: ISenderDetail;
  generatedRedactedEmailList: GeneratedRedactedEmailHint[];
  verificationMessage?: string;
  /** Do not render the card */
  inline?: boolean;
}

const selectClassName =
  `mt-1 block w-full rounded-md border-gray-300 bg-white py-2 pl-3 pr-10 text-sm ` +
  `focus:border-paidniceGreen-500 focus:outline-hidden focus:ring-paidniceGreen-500 ` +
  `disabled:cursor-not-allowed disabled:bg-gray-100`;

const inputClassName =
  `mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm ` +
  `text-gray-900 shadow-xs focus:border-paidniceGreen-500 focus:outline-hidden ` +
  `focus:ring-paidniceGreen-500 disabled:cursor-not-allowed disabled:bg-gray-100`;

function requestVerificationPath(resourceType: PortalVerifyResource, code: string) {
  if (resourceType === `invoice`) {
    return `invoice/code/${code}/request-verification`;
  }

  if (resourceType === `contact`) {
    return `contact/code/${code}/request-verification`;
  }

  return `quote/code/${code}/request-verification`;
}

function normalizePortalAuthCode(rawInput: string) {
  const trimmedInput = rawInput.trim();

  if (!trimmedInput) {
    return null;
  }

  try {
    const parsed = new URL(trimmedInput);
    const codeFromUrl = parsed.searchParams.get(`portalAuthCode`)?.trim();

    if (codeFromUrl) {
      return codeFromUrl;
    }
  }
  catch {
    // Not a full URL, continue parsing fallback formats.
  }

  if (trimmedInput.includes(`portalAuthCode=`)) {
    const queryString = trimmedInput.includes(`?`)
      ? trimmedInput.split(`?`)[1]
      : trimmedInput;
    const codeFromQuery = new URLSearchParams(queryString).get(`portalAuthCode`)?.trim();

    if (codeFromQuery) {
      return codeFromQuery;
    }
  }

  return trimmedInput;
}

export function Verify({ resourceType, senderDetail, generatedRedactedEmailList, verificationMessage, inline }: VerifyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedIdentifier, setSelectedIdentifier] = useState(``);
  const [manualCodeInput, setManualCodeInput] = useState(``);
  const [showManualCodeForm, setShowManualCodeForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [manualCodeError, setManualCodeError] = useState<string | null>(null);
  const { code } = useParams<{ code?: string}>();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErrorMessage(null);
    setLoading(true);

    try {
      await axios({
        method: `POST`,
        url: `${env.basePortalApiUrl}${requestVerificationPath(resourceType, code)}`,
        data: {
          verificationEmailIdentifier: selectedIdentifier,
        },
        withCredentials: true,
      });

      setSuccess(true);
    }
    catch (err: unknown) {
      if (axios.isAxiosError(err)) {
        const msg =
          typeof err.response?.data === `string`
            ? err.response.data
            : (err.response?.data as { message?: string } | undefined)?.message || err.message;

        setErrorMessage(msg || `Something went wrong`);
      }
      else {
        setErrorMessage(`Something went wrong`);
      }
    }
    finally {
      setLoading(false);
    }
  }

  function onManualCodeSubmit(e: FormEvent) {
    e.preventDefault();
    setManualCodeError(null);

    const normalizedCode = normalizePortalAuthCode(manualCodeInput);

    if (!normalizedCode) {
      setManualCodeError(`Enter a valid verification code.`);

      return;
    }

    sessionStorage.setItem(PORTAL_AUTH_CODE_STORAGE_KEY, normalizedCode);

    const next = new URLSearchParams(searchParams);

    next.set(`portalAuthCode`, normalizedCode);

    setSearchParams(next, {
      replace: true,
    });
  }

  const CardWrapper = inline ? React.Fragment : Card;
  const CardContentWrapper = inline ? React.Fragment : CardContent;
  const verifyShellClass = classNames(
    `mx-auto w-full`,
    inline ? `` : `max-w-md px-4 py-10 sm:py-14`,
  );

  const manualCodeForm = (
    <form
      className={ `space-y-3` }
      onSubmit={ onManualCodeSubmit }
    >
      <Paragraph className={ `text-center text-gray-600` }>
        { `Enter the verification code from your email.` }
      </Paragraph>
      <div>
        <FormLabel name={ `manual-verification-code` }>
          { `Verification code` }
        </FormLabel>
        <input
          id={ `manual-verification-code` }
          name={ `manual-verification-code` }
          className={ inputClassName }
          value={ manualCodeInput }
          onChange={ e => setManualCodeInput(e.target.value) }
          placeholder={ `Paste code or full verification URL` }
          autoComplete={ `off` }
        />
      </div>

      { manualCodeError ? (
        <Notification type={ `error` }>
          { manualCodeError }
        </Notification>
      ) : null }

      <div className={ `flex justify-center` }>
        <Button
          color={ `paidniceGreen` }
          type={ `submit` }
          disabled={ !manualCodeInput.trim() }
        >
          { `Verify with code` }
        </Button>
      </div>
    </form>
  );

  if (success) {
    return (
      <div className={ classNames(verifyShellClass, `flex flex-col items-stretch`) }>
        <Heading
          as={ `h1` }
          size={ `lg` }
          className={ `mb-4 text-center` }
        >
          { senderDetail.customerFacingCompanyName }
        </Heading>
        <Notification type={ `success` }>
          { `A verification link has been sent to the email address you selected` }
        </Notification>

        <div className={ `my-6 border-t border-gray-200` } />

        { manualCodeForm }
      </div>
    );
  }

  if (generatedRedactedEmailList.length === 0) {
    return (
      <div className={ verifyShellClass }>
        <CardWrapper>
          <CardContentWrapper>
            <Heading
              as={ `h1` }
              size={ `lg` }
              className={ `mb-3 text-center` }
            >
              { senderDetail.customerFacingCompanyName }
            </Heading>
            <Notification type={ `warning` }>
              { `No email addresses are available to verify with. Please contact ` }
              <span className={ `font-medium` }>
                { senderDetail.customerFacingCompanyName }
              </span>
              { ` for help.` }
            </Notification>
          </CardContentWrapper>
        </CardWrapper>
      </div>
    );
  }

  return (
    <div className={ verifyShellClass }>
      <CardWrapper>
        <CardContentWrapper>
          <form
            className={ `space-y-3` }
            onSubmit={ onSubmit }
          >
            <Heading
              as={ `h1` }
              size={ `lg` }
              className={ `text-center` }
            >
              { senderDetail.customerFacingCompanyName }
            </Heading>
            { verificationMessage ? (
              <Notification type={ `warning` }>
                { verificationMessage }
              </Notification>
            ) : null }
            <Paragraph className={ `text-center text-gray-600` }>
              { `Select the email address we should send your verification link to for documents from ` }
              <span className={ `font-medium text-gray-900` }>
                { senderDetail.customerFacingCompanyName }
              </span>
              { `.` }
            </Paragraph>

            <div>
              <FormLabel name={ `verification-email` }>
                { `Email address` }
              </FormLabel>
              <select
                id={ `verification-email` }
                name={ `verification-email` }
                className={ selectClassName }
                value={ selectedIdentifier }
                onChange={ e => setSelectedIdentifier(e.target.value) }
                required
                disabled={ loading }
              >
                <option value={ `` }
                  disabled>
                  { `Choose an address…` }
                </option>
                { generatedRedactedEmailList.map(entry => (
                  <option
                    key={ entry.identifier }
                    value={ entry.identifier }
                  >
                    { entry.redactedAddress }
                  </option>
                )) }
              </select>
            </div>

            { errorMessage ? (
              <Notification type={ `error` }>
                { errorMessage }
              </Notification>
            ) : null }

            <div className={ `flex justify-center` }>
              <Button
                color={ `paidniceGreen` }
                type={ `submit` }
                loading={ loading }
                disabled={ loading || !selectedIdentifier }
              >
                { `Send verification link` }
              </Button>
            </div>
          </form>

          <div className={ `mt-5 text-center` }>
            <button
              type={ `button` }
              className={ `text-sm text-paidniceGreen-700 underline hover:text-paidniceGreen-800` }
              onClick={ () => setShowManualCodeForm(current => !current) }
            >
              { showManualCodeForm ? `Hide manual code entry` : `Enter code manually` }
            </button>
          </div>

          { showManualCodeForm ? (
            <>
              <div className={ `my-6 border-t border-gray-200` } />

              { manualCodeForm }
            </>
          ) : null }
        </CardContentWrapper>
      </CardWrapper>
    </div>
  );
}
